import { cn } from '@/utils/classUtils';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import type { FC, ReactNode } from 'react';
import {
  REGULATORY_WATCH_BOTTOM_BAR_HEIGHT_PX,
  REGULATORY_WATCH_TABLE_PAGE_SIZE,
} from '@/components/table/constants';

export type TablePaginationStickyFooterProps = {
  totalCount: number;
  pageIndex: number;
  pageSize?: number;
  onPageIndexChange: (pageIndex: number) => void;
  /** Lowercase plural noun, e.g. "references" → `1-200 of 387 references` */
  entityLabel: string;
  className?: string;
};

/** Paragraph small / regular — Inter Regular 14px, 150% line height, 0.5% tracking */
const labelClasses =
  'text-sm font-normal leading-[1.5] tracking-[0.005em] text-neutral-900';

const FooterIconButton: FC<{
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}> = ({ label, disabled, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'inline-flex size-8 shrink-0 items-center justify-center rounded border border-[#e5e5e5] bg-white shadow-[0_1px_1px_rgba(0,0,0,0.05)] transition-colors',
      'enabled:hover:bg-neutral-50 enabled:active:bg-neutral-100',
      'disabled:cursor-not-allowed disabled:opacity-50'
    )}
  >
    <span className="sr-only">{label}</span>
    <span className={cn('[&_svg]:size-[14px]', disabled && 'text-neutral-400')}>
      {children}
    </span>
  </button>
);

export const TablePaginationStickyFooter: FC<
  TablePaginationStickyFooterProps
> = ({
  totalCount,
  pageIndex,
  pageSize = REGULATORY_WATCH_TABLE_PAGE_SIZE,
  onPageIndexChange,
  entityLabel,
  className,
}) => {
  if (totalCount <= 0) {
    return null;
  }

  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePageIndex = Math.min(Math.max(0, pageIndex), pageCount - 1);
  const start = safePageIndex * pageSize + 1;
  const end = Math.min((safePageIndex + 1) * pageSize, totalCount);
  const showPaginationControls = totalCount >= REGULATORY_WATCH_TABLE_PAGE_SIZE;

  return (
    <div
      className={cn(
        // px-6 matches Regulatory Watch layout header, filters, and table scroll gutters (24px).
        'flex shrink-0 items-center border-t border-neutral-200 bg-white px-6',
        className
      )}
      style={{
        height: REGULATORY_WATCH_BOTTOM_BAR_HEIGHT_PX,
      }}
    >
      <div className="flex min-h-0 w-full items-center justify-between gap-4">
        <p className={cn(labelClasses, 'truncate')}>
          {start}-{end} of {totalCount} {entityLabel}
        </p>
        {showPaginationControls ? (
          <div className="flex shrink-0 items-center gap-3">
            <p className={cn(labelClasses, 'whitespace-nowrap')}>
              Page {safePageIndex + 1} of {pageCount}
            </p>
            <div className="flex items-center gap-1">
              <FooterIconButton
                label="First page"
                disabled={safePageIndex <= 0}
                onClick={() => onPageIndexChange(0)}
              >
                <ChevronsLeft strokeWidth={2} />
              </FooterIconButton>
              <FooterIconButton
                label="Previous page"
                disabled={safePageIndex <= 0}
                onClick={() => onPageIndexChange(safePageIndex - 1)}
              >
                <ChevronLeft strokeWidth={2} />
              </FooterIconButton>
              <FooterIconButton
                label="Next page"
                disabled={safePageIndex >= pageCount - 1}
                onClick={() => onPageIndexChange(safePageIndex + 1)}
              >
                <ChevronRight strokeWidth={2} />
              </FooterIconButton>
              <FooterIconButton
                label="Last page"
                disabled={safePageIndex >= pageCount - 1}
                onClick={() => onPageIndexChange(pageCount - 1)}
              >
                <ChevronsRight strokeWidth={2} />
              </FooterIconButton>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
